import React, { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Check, AlertCircle } from "lucide-react";
import PrimaryButton from "../PrimaryButton/PrimaryButton";
import PlainTextButton from "../PlainTextButton/PlainTextButton";
import OnboardingWizardLayout, { OnboardingLanguageSwitcher } from "../OnboardingFlow/OnboardingWizardLayout";
import { ONBOARDING_BRAND } from "../OnboardingFlow/onboardingBrand";
import SubscriptionsAPI from "../../api/SubscriptionsAPI";
import { generatePricingPlan, getThemeColorClasses, isLimitedFeature, sortIncludedFeaturesByAccess } from "../../utils/pricingPlanUtils";
import { PricingPlanType } from "../../types/pricingPlans";
import { getRecommendedPlanId } from "../../utils/onboardingFirstWin";
import { toastService } from "../../services/ToastService";

const PAID_PLANS: PricingPlanType[] = ["store", "brand", "advanced-store"];

const OnboardingPlanFlow: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [isLoading, setIsLoading] = useState(false);
  const recommendedPlanId = getRecommendedPlanId(location.state?.firstWin);

  const handleSubscribe = async (plan: PricingPlanType) => {
    setIsLoading(true);
    try {
      const response = await SubscriptionsAPI.createCheckoutOrPortal(plan);
      window.location.href = response.data.url;
    } catch (error) {
      console.error("Failed to start onboarding checkout", { plan, error });
      toastService.showToast(t("onboarding.plan.checkout_error") as string, "error");
      setIsLoading(false);
    }
  };

  const handleTryFree = () => {
    navigate("/");
  };

  const plans = PAID_PLANS.map((planId) => {
    const plan = generatePricingPlan(planId, false);
    return {
      ...plan,
      name: t(plan.name, plan.name),
      description: t(plan.description, plan.description),
      features: sortIncludedFeaturesByAccess(
        plan.features.map((featureKey) => ({
          key: featureKey,
          label: t(featureKey, featureKey),
        }))
      ),
      price: t(plan.price, plan.price) as string,
    };
  });

  return (
    <OnboardingWizardLayout
      title={t("onboarding.plan.title") as string}
      subtitle={t("onboarding.plan.subtitle") as string}
      maxWidthClass="max-w-6xl"
    >
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {plans.map((plan) => {
          const themeClasses = getThemeColorClasses(plan.themeColor);
          const isRecommended = plan.id === recommendedPlanId;
          return (
            <div
              key={plan.id}
              className={`relative rounded-2xl border p-6 flex flex-col ${
                isRecommended ? ONBOARDING_BRAND.recommendedCard : ONBOARDING_BRAND.idleSurface
              }`}
            >
              {isRecommended && (
                <span className={ONBOARDING_BRAND.recommendedBadge}>
                  {t("onboarding.plan.recommended")}
                </span>
              )}
              <h3 className={`text-xl font-bold ${themeClasses.text} mb-2 text-center`}>{plan.name}</h3>
              <p className="text-sm text-muted-foreground text-center mb-4">{plan.description}</p>
              <div className="text-center mb-4">
                <div className="text-3xl font-bold text-foreground">{plan.price}</div>
                <p className="text-sm text-muted-foreground">{t("pricing.per_month", "per month")}</p>
              </div>
              <ul className="space-y-2 flex-1 mb-6">
                {plan.features.map((feature) => (
                  <li key={feature.key} className="flex items-start gap-2 text-sm text-foreground">
                    {isLimitedFeature(feature.key) ? (
                      <AlertCircle className="w-4 h-4 mt-0.5 text-orange-500 shrink-0" />
                    ) : (
                      <Check className="w-4 h-4 mt-0.5 text-green-500 shrink-0" />
                    )}
                    <span>{feature.label}</span>
                  </li>
                ))}
              </ul>
              <div className="flex justify-center">
                <PrimaryButton
                  tone="brand"
                  text={t("onboarding.plan.choose", { plan: plan.name }) as string}
                  fn={() => handleSubscribe(plan.id)}
                  isDisabled={isLoading}
                  actionName={`onboarding_plan_subscribe_${plan.id}`}
                />
              </div>
              <p className="text-xs text-muted-foreground text-center mt-3">
                {t("onboarding.plan.cancel_anytime")}
              </p>
            </div>
          );
        })}
      </div>
      <div className="flex flex-col items-center mt-8 space-y-3">
        <PlainTextButton
          tone="brand"
          text={t("onboarding.plan.try_free") as string}
          onClick={handleTryFree}
          actionName="onboarding_plan_try_free"
        />
        <OnboardingLanguageSwitcher />
      </div>
    </OnboardingWizardLayout>
  );
};

export default OnboardingPlanFlow;
